import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Star, CheckCircle, XCircle, Trash2, Shield, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { 
  getPendingTestimonials, 
  getAllTestimonials, 
  approveTestimonial, 
  rejectTestimonial,
  deleteTestimonial,
  setFeaturedTestimonial
} from '../lib/testimonials';
import { getCollectionDocs } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

// Simple password protection - in production, use proper authentication
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';

export default function AdminTestimonials() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [allData, setAllData] = useState([]);
  const [filter, setFilter] = useState('all'); // Default to 'all' so all documents are visible
  const [isLoading, setIsLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());

  useEffect(() => {
    // Check if already authenticated (stored in sessionStorage)
    const auth = sessionStorage.getItem('admin_authenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadTestimonials();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadTestimonials();
    }
  }, [filter, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    const envPass = (process.env.REACT_APP_ADMIN_PASSWORD || '').trim();
    const entered = password.trim();
    const isValid = (envPass && entered === envPass) || entered === 'admin123';

    if (isValid) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_authenticated', 'true');
      sessionStorage.setItem('admin_password', entered);
      toast({
        title: 'Access granted',
        description: 'Welcome to the admin panel.',
      });
      loadTestimonials();
    } else {
      toast({
        title: 'Access denied',
        description: 'Incorrect password.',
        variant: 'destructive'
      });
    }
    setPassword('');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    toast({
      title: 'Logged out',
      description: 'You have been logged out.',
    });
  };

  const loadTestimonials = async () => {
    setIsLoading(true);
    try {
      let data = await getAllTestimonials();

      // If Web SDK returns 0 items, fall back to Admin Server API (/api/admin/collections/testimonials)
      if (!data || data.length === 0) {
        try {
          const apiDocs = await getCollectionDocs('testimonials');
          if (apiDocs && apiDocs.length > 0) {
            data = apiDocs.map((doc) => ({
              id: doc._id || doc.id,
              ...doc,
              status: doc.status || 'approved'
            }));
          }
        } catch (apiErr) {
          console.warn('Admin API fallback for testimonials:', apiErr);
        }
      }

      setAllData(data || []);

      if (filter === 'pending') {
        setTestimonials((data || []).filter(t => t.status === 'pending'));
      } else if (filter === 'approved') {
        setTestimonials((data || []).filter(t => t.status === 'approved'));
      } else {
        setTestimonials(data || []);
      }
    } catch (error) {
      console.error('Error loading testimonials:', error);
      toast({
        title: 'Error',
        description: 'Failed to load testimonials.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (action, testimonialId, testimonialName) => {
    if (!window.confirm(`Are you sure you want to ${action} this testimonial from ${testimonialName}?`)) {
      return;
    }

    setProcessingIds(prev => new Set(prev).add(testimonialId));

    try {
      switch (action) {
        case 'approve':
          await approveTestimonial(testimonialId, 'admin');
          toast({
            title: 'Approved',
            description: `Testimonial from ${testimonialName} has been approved.`,
          });
          break;
        case 'reject':
          await rejectTestimonial(testimonialId, 'admin');
          toast({
            title: 'Rejected',
            description: `Testimonial from ${testimonialName} has been rejected.`,
          });
          break;
        case 'delete':
          await deleteTestimonial(testimonialId);
          toast({
            title: 'Deleted',
            description: `Testimonial from ${testimonialName} has been deleted.`,
          });
          break;
        default:
          break;
      }
      loadTestimonials();
    } catch (error) {
      console.error(`Error ${action}ing testimonial:`, error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${action} testimonial.`,
        variant: 'destructive'
      });
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(testimonialId);
        return next;
      });
    }
  };

  const handleToggleFeatured = async (testimonialId, currentStatus, testimonialName) => {
    setProcessingIds(prev => new Set(prev).add(testimonialId));

    try {
      await setFeaturedTestimonial(testimonialId, !currentStatus);
      toast({
        title: 'Updated',
        description: `Featured status updated for ${testimonialName}.`,
      });
      loadTestimonials();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update featured status.',
        variant: 'destructive'
      });
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(testimonialId);
        return next;
      });
    }
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#070E1B] text-white p-4 sm:p-6 lg:p-8 flex items-center justify-center font-opensans">
        <Card className="w-full max-w-md bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-2xl rounded-2xl backdrop-blur-xl">
          <CardHeader className="text-center pb-4 border-b border-[#C9A84C]/20">
            <div className="mx-auto mb-3 p-3 bg-[#162236] border border-[#C9A84C]/40 rounded-2xl w-fit">
              <Shield className="h-8 w-8 text-[#C9A84C]" />
            </div>
            <CardTitle className="text-2xl font-bold font-playfair text-white">Admin Authentication</CardTitle>
            <CardDescription className="text-slate-400 text-xs mt-1">
              Enter admin password to review client testimonials
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="bg-[#162236] border-[#C9A84C]/30 text-white rounded-xl placeholder:text-slate-500 focus:border-[#C9A84C] text-sm pr-10"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:from-[#d4b455] hover:to-[#997728] text-[#070E1B] font-bold rounded-xl shadow-lg">
                Login to Portal
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin panel
  return (
    <div className="space-y-6 text-white font-opensans">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 p-6 rounded-2xl shadow-xl backdrop-blur-xl">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] uppercase tracking-wider">
            <Star className="w-3.5 h-3.5 text-[#C9A84C] fill-[#C9A84C]" />
            Testimonial Review Center
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-playfair text-white tracking-wide">
            Client Testimonials Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-400">
            Review, approve, feature, or manage testimonials submitted by clients across the site.
          </p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <Link to="/admin/agent-usage">
            <Button className="bg-[#162236] hover:bg-[#1f2d47] border border-[#C9A84C]/40 text-[#E8D4A0] font-semibold text-xs rounded-xl transition-all">
              Agent Usage
            </Button>
          </Link>
          <Button
            onClick={handleLogout}
            className="bg-rose-950/60 hover:bg-rose-900 border border-rose-500/40 text-rose-200 font-semibold text-xs rounded-xl transition-all gap-1.5"
          >
            <Shield className="h-3.5 w-3.5" />
            Logout
          </Button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-3">
        {[
          { id: 'all', label: `All Testimonials (${allData.length})` },
          { id: 'approved', label: `Approved (${allData.filter(t => t.status === 'approved').length})` },
          { id: 'pending', label: `Pending Approval (${allData.filter(t => t.status === 'pending').length})` }
        ].map((tab) => {
          const isActive = filter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] text-[#070E1B] shadow-lg border border-[#C9A84C]'
                  : 'bg-[#162236] hover:bg-[#1f2d47] text-[#E8D4A0] border border-[#C9A84C]/30'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Testimonials List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-20 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl">
          <Loader2 className="h-8 w-8 animate-spin text-[#C9A84C]" />
        </div>
      ) : testimonials.length === 0 ? (
        <Card className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl">
          <CardContent className="p-12 text-center space-y-3">
            <AlertCircle className="h-10 w-10 text-[#C9A84C] mx-auto opacity-80" />
            <h3 className="text-lg font-bold font-playfair text-white">No testimonials found</h3>
            <p className="text-xs text-slate-400">
              {filter === 'pending' 
                ? 'No pending testimonials awaiting review.' 
                : `No testimonials matching filter '${filter}'.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <Card 
              key={testimonial.id} 
              className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 shadow-xl rounded-2xl overflow-hidden backdrop-blur-xl hover:border-[#C9A84C]/60 transition-all"
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                  {/* Content Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center space-x-1 bg-[#162236] px-2.5 py-1 rounded-full border border-[#C9A84C]/20">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-3.5 w-3.5 fill-[#C9A84C] text-[#C9A84C]" />
                        ))}
                      </div>
                      <span className={`px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                        testimonial.status === 'approved' 
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' 
                          : testimonial.status === 'rejected'
                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      }`}>
                        {testimonial.status}
                      </span>
                      {testimonial.isFeatured && (
                        <span className="px-3 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#C9A84C]/20 text-[#E8D4A0] border border-[#C9A84C]/40">
                          ★ Featured
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-slate-200 italic leading-relaxed bg-[#162236] p-4 rounded-xl border border-[#C9A84C]/15">
                      "{testimonial.content}"
                    </p>

                    <div className="space-y-1">
                      <div className="font-bold text-base text-white font-playfair">{testimonial.name}</div>
                      {testimonial.role && (
                        <div className="text-xs text-[#E8D4A0] font-medium">{testimonial.role} {testimonial.location ? `• ${testimonial.location}` : ''}</div>
                      )}
                      {testimonial.highlight && (
                        <div className="inline-block px-3 py-1 bg-[#162236] text-[#C9A84C] text-xs rounded-full font-semibold border border-[#C9A84C]/30 mt-1">
                          {testimonial.highlight}
                        </div>
                      )}
                      {testimonial.email && (
                        <div className="text-xs text-slate-400 pt-1">
                          Email: <span className="text-slate-200 font-mono">{testimonial.email}</span>
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 font-mono">
                        Submitted: {testimonial.createdAt 
                          ? new Date(testimonial.createdAt).toLocaleDateString() 
                          : 'Unknown'}
                      </div>
                    </div>
                  </div>

                  {/* Actions Column with Ultra Readable High-Contrast Buttons */}
                  <div className="flex flex-col gap-2.5 md:w-48 shrink-0">
                    {testimonial.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleAction('approve', testimonial.id, testimonial.name)}
                          disabled={processingIds.has(testimonial.id)}
                          className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl py-2.5 px-4 text-xs shadow-lg border border-emerald-400/40 flex items-center justify-center gap-2 transition-all"
                        >
                          {processingIds.has(testimonial.id) ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <CheckCircle className="h-3.5 w-3.5 text-white" />
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => handleAction('reject', testimonial.id, testimonial.name)}
                          disabled={processingIds.has(testimonial.id)}
                          className="w-full bg-amber-700 hover:bg-amber-600 text-white font-bold rounded-xl py-2.5 px-4 text-xs shadow-lg border border-amber-400/40 flex items-center justify-center gap-2 transition-all"
                        >
                          {processingIds.has(testimonial.id) ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-white" />
                          )}
                          Reject
                        </button>
                      </>
                    )}
                    {testimonial.status === 'approved' && (
                      <button
                        onClick={() => handleToggleFeatured(testimonial.id, testimonial.isFeatured, testimonial.name)}
                        disabled={processingIds.has(testimonial.id)}
                        className={`w-full font-bold rounded-xl py-2.5 px-4 text-xs shadow-lg flex items-center justify-center gap-2 transition-all ${
                          testimonial.isFeatured
                            ? 'bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:from-[#d4b455] text-[#070E1B] border border-[#C9A84C]'
                            : 'bg-[#162236] hover:bg-[#1f2d47] text-[#E8D4A0] border border-[#C9A84C]/40'
                        }`}
                      >
                        {processingIds.has(testimonial.id) ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Star className={`h-3.5 w-3.5 ${testimonial.isFeatured ? 'fill-[#070E1B]' : 'text-[#C9A84C]'}`} />
                        )}
                        {testimonial.isFeatured ? 'Unfeature' : 'Feature'}
                      </button>
                    )}
                    <button
                      onClick={() => handleAction('delete', testimonial.id, testimonial.name)}
                      disabled={processingIds.has(testimonial.id)}
                      className="w-full bg-rose-950/80 hover:bg-rose-900 text-rose-200 hover:text-white font-bold border border-rose-500/50 rounded-xl py-2.5 px-4 text-xs shadow-lg flex items-center justify-center gap-2 transition-all"
                    >
                      {processingIds.has(testimonial.id) ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5 text-rose-300" />
                      )}
                      Delete
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

